import clsx from "clsx";
import { type JSX, useState } from "react";

import { ManageSubscriptionDialog } from "features/subscriptions/components";

import { Button } from "common/components/ui/Button";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import SubscriptionDetailsCard from "../../../SubscriptionDetailsCard";
import NonRenewingTag from "../NonRenewingTag";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionInfo from "../SubscriptionInfo";
import WorkspaceSubRenewalPrice from "../WorkspaceSubRenewalPrice";
import Accounts from "./Accounts";
import ScheduledChangeTag from "./ScheduledChangeTag";

export default function Rented() {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const { id: workspaceId, rental_subscription_status } =
    useSelectedWorkspaceContext();
  const { workspace, isLoadingWorkspace } = useWorkspaceDetails(workspaceId);

  let priceComponent: JSX.Element;
  let tagComponent = <Tag variant="disabled">Inactive</Tag>;
  let buttonComponent = (
    <Button onClick={() => setIsOpen(true)} size={isDesktop ? "sm" : "md"}>
      Activate subscription
    </Button>
  );

  if (rental_subscription_status) {
    if (isLoadingWorkspace) {
      buttonComponent = (
        <Skeleton className={clsx(isDesktop ? "h-8 w-40" : "h-10 w-40")} />
      );
      tagComponent = <Skeleton className="w-15 h-6" />;
    } else if (workspace) {
      const { rental_subscription } = workspace;
      const { status, has_changes } = rental_subscription;

      if (status === "cancelled") {
        buttonComponent = (
          <Button
            onClick={() => setIsOpen(true)}
            size={isDesktop ? "sm" : "md"}
          >
            Reactivate subscription
          </Button>
        );
      } else if (status === "non_renewing") {
        buttonComponent = (
          <RemoveChangeButton
            subscription={workspace.rental_subscription}
            type="cancellation"
          />
        );
        tagComponent = <NonRenewingTag />;
      } else if (has_changes) {
        buttonComponent = (
          <RemoveChangeButton
            subscription={rental_subscription}
            type="change"
          />
        );
        tagComponent = <ScheduledChangeTag />;
      } else {
        priceComponent = (
          <WorkspaceSubRenewalPrice
            subscriptionId={rental_subscription.id}
            pricingPeriodInfo={rental_subscription.item_price}
          />
        );
        tagComponent =
          workspace.rental_subscription.item_price.period === 6 ? (
            <Tag variant="gradient">6 months deal</Tag>
          ) : (
            <Tag variant="success">Active</Tag>
          );
        buttonComponent = (
          <Button
            onClick={() => setIsOpen(true)}
            size={isDesktop ? "sm" : "md"}
          >
            Manage subscription
          </Button>
        );
      }
    }
  }

  return (
    <>
      <SubscriptionDetailsCard
        className="relative before:absolute before:size-full before:left-0 before:rounded-3xl before:top-4 before:-z-10 before:blur-[9px] before:bg-[conic-gradient(from_180deg_at_50%_50%,#FDE8FF_0deg,#E0E1FF_54.48deg,#DAF5FF_148.92deg,#FFF8DB_220.96deg,#FFF0E0_291.98deg,#FDE8FF_360deg)]"
        plan="rental"
        footerComponent={
          <SubscriptionInfo
            isLoading={isLoadingWorkspace}
            subscription={workspace?.rental_subscription}
            buttonComponent={buttonComponent}
          />
        }
        tagComponent={tagComponent}
        priceComponent={priceComponent}
        mainComponent={rental_subscription_status && <Accounts />}
      />
      <ManageSubscriptionDialog
        plan="rental"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  );
}
